import { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../../primitives/dialog";
import { Button } from "../../primitives/button";
import { Input } from "../../primitives/input";
import { Label } from "../../primitives/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../primitives/select";
import { Copy, Check } from "lucide-react";
import { useDataAdapter } from "@shared-core/data/DataAdapterContext";
import { usePlatformCapabilities } from "@shared-core/capabilities/context";
import { toast } from "sonner";

interface SharePreviewDialogProps {
  open: boolean;
  onClose: () => void;
  repository: string;
  branchName: string;
}

export function SharePreviewDialog({
  open,
  onClose,
  repository,
  branchName,
}: SharePreviewDialogProps) {
  const dataAdapter = useDataAdapter();
  const capabilities = usePlatformCapabilities();
  const [visibility, setVisibility] = useState("token");
  const [expiresAt, setExpiresAt] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [shareUrl, setShareUrl] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  async function handleShare() {
    setSubmitting(true);
    try {
      const result = await dataAdapter.sharePreview(repository, branchName, {
        visibility_scope: visibility,
        expires_at: expiresAt || null,
      });
      const url = `${capabilities.getShareOrigin()}/previews/${result.share_token}`;
      setShareUrl(url);
      toast.success("Share link created");
    } catch (error) {
      console.error("Error sharing preview:", error);
      toast.error("Failed to create share link");
    } finally {
      setSubmitting(false);
    }
  }

  async function handleCopy() {
    if (!shareUrl) return;
    try {
      await capabilities.copyToClipboard(shareUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error("Failed to copy to clipboard");
    }
  }

  function handleClose() {
    setShareUrl(null);
    setCopied(false);
    onClose();
  }

  return (
    <Dialog open={open} onOpenChange={(v) => !v && handleClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Share Preview</DialogTitle>
        </DialogHeader>

        {shareUrl ? (
          <div className="space-y-4 py-2">
            <p className="text-sm text-muted-foreground">
              Anyone with this link can view the preview (read-only).
            </p>
            <div className="flex gap-2">
              <Input value={shareUrl} readOnly className="text-xs" />
              <Button variant="outline" size="icon" onClick={handleCopy}>
                {copied ? (
                  <Check className="size-4 text-green-600" />
                ) : (
                  <Copy className="size-4" />
                )}
              </Button>
            </div>
          </div>
        ) : (
          <div className="space-y-4 py-2">
            <div className="space-y-2">
              <Label>Access scope</Label>
              <Select value={visibility} onValueChange={setVisibility}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="token">Anyone with link</SelectItem>
                  <SelectItem value="team">Team members</SelectItem>
                  <SelectItem value="org">Organisation</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="expires">Expires at (optional)</Label>
              <Input
                id="expires"
                type="datetime-local"
                value={expiresAt}
                onChange={(e) => setExpiresAt(e.target.value)}
              />
            </div>
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={handleClose}>
            {shareUrl ? "Done" : "Cancel"}
          </Button>
          {!shareUrl && (
            <Button onClick={handleShare} disabled={submitting}>
              {submitting ? "Creating..." : "Create Share Link"}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
